import { JOB_AGGREGATION_SPEED_SECOND_PER_JOB } from "./project-summary/ProjectSummarySidebarHostedScraper";

const SECONDS_PER_JOB = 30;
const ESTIMATED_TIME_BEFORE_FIRST_JOB_SCRAPED = 40;
const ESTIMATED_TIME_BEFORE_FINALIZATION_STARTS = 30;

export type JobRunTimeEstimate = {
  remainingTotalSeconds: number;
  totalEstimatedSeconds: number;
  progressPercentage: number;
};

const estimateJobRunTimeWithoutTimeSpent = (
  userConcurrencyLimit: number,
  totalJobsCount: number,
  finishedJobsCount: number,
  finalizedJobsCount: number | undefined,
): JobRunTimeEstimate => {
  const remainingJobsToScrape = totalJobsCount - finishedJobsCount;
  const remainingJobsToFinalize = totalJobsCount - (finalizedJobsCount ?? 0);
  const timePerJob = SECONDS_PER_JOB / userConcurrencyLimit;

  const spentTotalSeconds =
    timePerJob * finishedJobsCount
    + (finalizedJobsCount ?? 0) * JOB_AGGREGATION_SPEED_SECOND_PER_JOB
    + (finishedJobsCount > 0 ? ESTIMATED_TIME_BEFORE_FIRST_JOB_SCRAPED : 0)
    + (finalizedJobsCount && finalizedJobsCount > 0 ? ESTIMATED_TIME_BEFORE_FINALIZATION_STARTS : 0);

  const remainingTotalSeconds =
    timePerJob * remainingJobsToScrape
    + remainingJobsToFinalize * JOB_AGGREGATION_SPEED_SECOND_PER_JOB
    + (finishedJobsCount === 0 ? ESTIMATED_TIME_BEFORE_FIRST_JOB_SCRAPED : 0)
    + (!finalizedJobsCount || finalizedJobsCount === 0 ? ESTIMATED_TIME_BEFORE_FINALIZATION_STARTS : 0);

  const progressPercentage = Math.ceil((spentTotalSeconds / (spentTotalSeconds + remainingTotalSeconds)) * 100);

  return { remainingTotalSeconds, totalEstimatedSeconds: spentTotalSeconds + remainingTotalSeconds, progressPercentage };
}

export const estimateJobRunTimeKnowingTheSpentTime = (
  jobStartedAt: Date,
  totalJobsCount: number,
  finishedJobsCount: number,
  finalizedJobsCount: number | undefined,
): JobRunTimeEstimate => {
  const secondsPassed = Math.max(0, ( Date.now() - new Date(jobStartedAt).getTime()) / 1000);

  const stillScraping = finishedJobsCount < totalJobsCount;

  if (stillScraping) {
    // We know finishedJobCount is not zero
    const timePerJob = secondsPassed / finishedJobsCount;
    const remainingJobsToScrape = totalJobsCount - finishedJobsCount;
    const remainingTotalSeconds = (timePerJob * remainingJobsToScrape) + ESTIMATED_TIME_BEFORE_FINALIZATION_STARTS + (totalJobsCount * JOB_AGGREGATION_SPEED_SECOND_PER_JOB);
    const progressPercentage = Math.floor((secondsPassed / (secondsPassed + remainingTotalSeconds)) * 100);
    return { remainingTotalSeconds, totalEstimatedSeconds: secondsPassed + remainingTotalSeconds, progressPercentage };
  } else {
    const remainingJobsToFinalize = totalJobsCount - (finalizedJobsCount ?? 0);
    const remainingTotalSeconds = remainingJobsToFinalize * JOB_AGGREGATION_SPEED_SECOND_PER_JOB;
    const progressPercentage = Math.floor((secondsPassed / (secondsPassed + remainingTotalSeconds)) * 100);
    return { remainingTotalSeconds, totalEstimatedSeconds: secondsPassed + remainingTotalSeconds, progressPercentage };
  }

}

export const estimateJobRunTime = (
  userConcurrencyLimit: number,
  jobStartedAt: Date | null |  undefined,
  totalJobsCount: number,
  finishedJobsCount: number,
  finalizedJobsCount: number | undefined,
): JobRunTimeEstimate => {

  if (jobStartedAt !== undefined && jobStartedAt !== null && finishedJobsCount > Math.ceil(totalJobsCount * 0.2) && finishedJobsCount > 0) {
    return estimateJobRunTimeKnowingTheSpentTime(jobStartedAt, totalJobsCount, finishedJobsCount, finalizedJobsCount);
  } else {
    return estimateJobRunTimeWithoutTimeSpent(userConcurrencyLimit, totalJobsCount, finishedJobsCount, finalizedJobsCount);
  }

};
